<template>
  <!-- handles Usage Section for all artifacts, some specials for Value Set Expansions -->
  <div>
    <TerminologyExpansionSetDialog
      v-if="modals.expansions.expansion"
      ref="expansionDialog"
      :expansion="modals.expansions.expansion"
      @close="closeExpansionDialog"
      :readonly="!userIsEditor"
    />
    <v-expansion-panels
      v-model="panelIsExpanded"
      v-if="!activeItemIsLoading"
      class="my-5">
      <v-expansion-panel @change="fetchUsageList">
        <v-expansion-panel-header>
          <div>
          {{$t('usage')}}<span v-if="usageList.length" class="px-1">({{associationsCounter}})</span>
          </div>
          <v-spacer/>
          <v-scale-transition hide-on-leave>
            <AdButton
              v-if="panelIsExpanded === 0"
              class="flex-grow-0"
              type="refresh"
              list
              @click.stop="fetchUsageList({ refresh: true })"
              :spinning="refreshing"
              :loading="refreshing"
            />
          </v-scale-transition>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <PanelLoading linear :loading="loadingUsage && !usageList.length" />
          <template v-for="association in associations">

            <v-card v-for="key in Object.keys(association)" :key="key" outlined class="mb-5">
              <v-data-table
                :headers="headers(key)"
                :items="association[key]"
                :hide-default-footer="association[key].length <= 10"
                class="elevation-1"
                :loading="modals.expansions.loading || loadingDeleteExpansionSet"
              >
                <template v-slot:[`item.name`]="{ item }">
                  <AdStatusDot v-if="item.targetStatusCode" class="mr-1" tooltip larger :status="item.targetStatusCode" :icon="getIcon(item, key)" />
                  <AdButton v-if="item.targetId" link :to="routeObject(item, key)">
                      {{ $filters.getMatchingTranslation({ value: item.name }) }}
                  </AdButton>
                  <span v-else>
                    {{ $filters.getMatchingTranslation({ value: item.name }) }}
                  </span>
                  <span v-if="item.targetEffectiveDate && !item.targetVersionLabel" class="ml-1 font-italic">{{$t('as-of')}} {{ $filters.formatDateShort(item.targetEffectiveDate)}}</span>
                  <span v-if="item.targetVersionLabel">({{ item.targetVersionLabel }})</span>

                </template>

                <template #item.expansionName="{ item }">
                  <AdStatusDot v-if="item.targetStatusCode" class="mr-1" tooltip larger :status="item.targetStatusCode" :icon="getIcon(item, key)" />
                  <AdButton link @click="openExpansionDialog(item.targetId)">
                    {{ $t('expansion-set') }}
                    {{ $filters.getMatchingTranslation({ value: item.name }) }}
                  </AdButton>
                  <span v-if="item.targetEffectiveDate" class="ml-1 font-italic">{{$t('as-of')}} {{ $filters.formatDateShort(item.targetEffectiveDate)}}</span>
                </template>

                <template v-slot:[`item.cardinality`]="{ item }">
                  {{cardinality(item)}}
                  <template v-if="cardinality(item) && conformance(item)">
                    <span> / </span>
                  </template>
                  {{conformance(item)}}
                </template>
                <template v-slot:[`item.pad`]="{ item }">
                  <div v-for="(path, i) in item.path" :key="`path-${i}`" class="my-1">
                    <TreeviewIcon v-if="path.type" :active-item="{ is: path.type }" artefact="TI"/>
                    {{ pathText(path) }}
                  </div>

                </template>
                <template v-slot:[`item.project`]="{ item }">
                  {{project(item)}}
                </template>
                <template #item.expansionActions="{item}">
                  <AdSpeedDial v-if="userIsEditor" direction="left">
                    <AdSpeedOption type="delete" :disabled="loadingDeleteExpansionSet" @click="deleteExpansionSet(item)" />
                  </AdSpeedDial>
                </template>
              </v-data-table>
            </v-card>

          </template>
          <AdInfoOwl chat v-if="!loadingUsage && !usageList.length">{{ $t('no-items') }}</AdInfoOwl>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

  </div>
</template>

<script>
import TerminologyExpansionSetDialog from '../Terminology/TerminologyExpansionSetDialog'
import TreeviewIcon from '@/components/Utilities/Treeview/TreeviewIcon'
import TerminologyService from '@/services/TerminologyService'

export default {
  name: 'UsageSection',
  components: { TerminologyExpansionSetDialog, TreeviewIcon },
  props: {
    expanded: {
      type: Boolean,
      default: false
    },
    // Id of the associated object
    objectId: {
      type: String,
      required: true
    },
    // Effective date of the associated object
    objectEffectiveDate: {
      type: String,
      default: null
    },
    action: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      usesTypes: [
        { key: 'transactionAssociation', label: 'transaction' },
        { key: 'templateAssociation', label: 'template' },
        { key: 'valueSetAssociation', label: 'valueset' },
        { key: 'conceptAssociation', label: 'concept' },
        { key: 'conceptMapAssociation', label: 'conceptmap' },
        { key: 'valueSetExpansionAssociation', label: 'expansion-set' }
      ],
      routes: {
        valueSetAssociation: {
          id: {
            name: 'terminology-value-set',
            props: [
              {
                name: 'id',
                prop: 'targetId'
              }
            ]
          },
          idAndVersion: {
            name: 'terminology-value-set-version',
            props: [
              {
                name: 'id',
                prop: 'targetId'
              },
              {
                name: 'version',
                prop: 'targetEffectiveDate'
              }
            ]
          }
        },
        valueSetExpansionAssociation: {
          id: {
            name: 'terminology-value-set',
            props: [
              {
                name: 'id',
                prop: 'targetId'
              }
            ]
          },
          idAndVersion: {
            name: 'terminology-value-set-version',
            props: [
              {
                name: 'id',
                prop: 'targetId'
              },
              {
                name: 'version',
                prop: 'targetEffectiveDate'
              }
            ]
          }
        },
        templateAssociation: {
          idAndVersion: {
            name: 'templates-details-id-effectiveDate',
            props: [
              {
                name: 'id',
                prop: 'targetId'
              },
              {
                name: 'effectiveDate',
                prop: 'targetEffectiveDate'
              }
            ]
          },
          id: {
            name: 'templates-details-id',
            props: [
              {
                name: 'id',
                prop: 'targetId'
              }
            ]
          }
        },
        transactionAssociation: {
          idAndVersion: {
            name: 'scenarios-details',
            props: [
              {
                name: 'scenarioId',
                prop: 'targetId'
              },
              {
                name: 'scenarioVersion',
                prop: 'targetEffectiveDate'
              }
            ]
          }
        },
        conceptAssociation: {
          idAndVersion: {
            name: 'dataset-details-version',
            props: [
              {
                name: 'conceptId',
                prop: 'targetId'
              },
              {
                name: 'conceptVersion',
                prop: 'targetEffectiveDate'
              },
              {
                name: 'datasetId',
                prop: 'targetDatasetId'
              },
              {
                name: 'datasetVersion',
                prop: 'targetDatasetEffectiveDate'
              }
            ]
          }
        },
        conceptMapAssociation: {
          idAndVersion: {
            name: 'concept-maps-details',
            props: [
              {
                name: 'conceptMapId',
                prop: 'targetId'
              },
              {
                name: 'conceptMapVersion',
                prop: 'targetEffectiveDate'
              }
            ]
          },
          id: {
            name: 'conceptmap-details-id',
            props: [
              {
                name: 'conceptMapId',
                prop: 'targetId'
              }
            ]
          }
        }
      },
      usageList: [],
      loadingDeleteExpansionSet: false,
      loadingUsage: false,
      refreshing: false,
      modals: {
        expansions: {
          expansion: null,
          loading: false
        }
      }
    }
  },
  computed: {
    transactionAssociationHeaders () {
      return [
        { text: this.$t('transaction'), value: 'name', width: '33.3%', sort: this.sortItemByName },
        { text: `${this.$t('cardinality')} / ${this.$t('conformance')}`, value: 'cardinality', width: '33.3%', sortable: false },
        { text: this.$t('project'), value: 'project', width: '33.3%', sortable: false }
      ]
        // Only show cardinality column if relevant
        .filter(header => header.value !== 'cardinality' || (this.usageList?.[0]?.transactionAssociation?.some(ass => ass.minimumMultiplicity != null && ass.maximumMultiplicity != null)))
    },
    templateAssociationHeaders () {
      return [
        { text: this.$t('template'), value: 'name', width: '33.3%', sort: this.sortItemByName },
        { text: `${this.$t('pad')} (${this.$t('cardconf')})`, value: 'pad', width: '33.3%', sortable: false },
        { text: this.$t('project'), value: 'project', width: '33.3%', sortable: false }
      ]
    },
    valueSetAssociationHeaders () {
      return [
        { text: this.$t('value-set'), value: 'name', width: '33.3%', sort: this.sortItemByName },
        { text: this.$t('id'), value: 'targetId', width: '33.3%', sortable: false },
        { text: this.$t('project'), value: 'project', width: '33.3%', sortable: false }
      ]
    },
    valueSetExpansionAssociationHeaders () {
      return [
        { text: this.$t('expansion-set'), value: 'expansionName', width: '60%', sort: this.sortItemByName },
        { text: this.$t('id'), value: 'targetId', width: '40%', sortable: false },
        { value: 'expansionActions', align: 'right', sortable: false }
      ]
    },
    conceptAssociationHeaders () {
      return [
        { text: this.$t('concept'), value: 'name', width: '33.3%', sort: this.sortItemByName },
        { text: this.$t('id'), value: 'targetId', width: '33.3%', sortable: false },
        { text: this.$t('project'), value: 'project', width: '33.3%', sortable: false }
      ]
    },
    conceptMapAssociationHeaders () {
      return [
        { text: this.$t('concept-map'), value: 'name', width: '33.3%', sort: this.sortItemByName },
        { text: this.$t('id'), value: 'targetId', width: '33.3%', sortable: false },
        { text: this.$t('project'), value: 'project', width: '33.3%', sortable: false }
      ]
    },
    associationsCounter () {
      let counter = 0
      if (this.associations) {
        this.associations.forEach((associationType) => {
          counter += Object.entries(associationType)?.[0]?.[1].length || 0
        })
      }
      return counter
    },
    associations () {
      return this.usageList
    },
    cardinality () {
      return (item) => this.$filters.getCardinality(item)
    },
    activeItemIsLoading () {
      return this.$store.state.dataset.loading.activeItem
    },
    conformanceTypes () {
      return this.$store.getters['server/getDecorTypes']?.data?.ConformanceType?.[0]?.enumeration?.map((e) => ({
        text: this.$filters.getMatchingTranslation({ value: e.label }),
        value: e.value
      })
      ) || []
    },
    conformanceType () {
      return (value) => {
        return this.conformanceTypes.find((type) => type.value === value)
      }
    },
    conformance () {
      /**
       * Only display conformance if isMandatory !== true
       */
      return (item) => {
        if (item?.isMandatory !== 'true') {
          const { conformance } = item
          if (conformance != null) {
            if (this.conformanceType(conformance)) {
              const result = this.conformanceType(conformance)
              return result.text
            } else {
              // in case the conformanceType is not offered in getDecorTypes,
              // fallback to value in offered by api
              return conformance
            }
          } else {
            return null
          }
        } else {
          // return translation for mandatory
          return this.$t('mandatory')
        }
      }
    },
    cardconf () {
      return (item) => {
        let result = null
        if (this.cardinality(item) != null) {
          result = ` (${this.cardinality(item)}`
        }
        if (this.conformance(item) != null) {
          result = ((result != null) ? ' / ' : '') + this.conformance(item)
        }
        if (result != null) {
          result += ')'
        }
        return result
      }
    },

    headers () {
      // helper to get the proper header for the table
      return (key) => {
        return this[`${key}Headers`]
      }
    },
    panelIsExpanded: {
      get () {
        return this.expanded ? 0 : null
      },
      set (value) {
        const expanded = value === 0
        this.$emit('update:expanded', expanded)
      }
    },
    pathText () {
      return path => path?.['#text']
    },
    project () {
      return (item) => {
        if (item?.projectName) {
          return this.$filters.getMatchingTranslation({ value: item.projectName })
        }
        if (item?.ident) {
          // could be linked to an other project
          return item.ident
        } else {
          // in 99.9% of the cases
          return this.$filters.getMatchingTranslation({ value: this.projectInFocus.name })
        }
      }
    }
  },
  methods: {
    async fetchUsageList ({ refresh = false } = {}) {
      if (this.panelIsExpanded === 0 && !refresh) {
        return
      }
      try {
        this.loadingUsage = true
        if (refresh) {
          this.refreshing = true
        }
        const prefix = this.projectInFocus?.prefix
        const usage = await this.$store.dispatch(this.action, {
          id: this.objectId,
          effectiveDate: this.objectEffectiveDate,
          params: {
            prefix
          }
        })

        const usageList = []
        this.usesTypes.forEach(type => {
          if (usage[type.key]) {
            usageList.push({ [type.key]: usage[type.key] })
          }
        })

        this.usageList = usageList
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        this.$oops(errorMessage)
      }
      this.loadingUsage = false
      if (refresh) {
        this.refreshing = false
      }
    },
    getIcon (item, associationType) {
      switch (associationType) {
        case 'transactionAssociation':
          return this.$filters.getScenarioIcon(item.targetType)
        case 'conceptAssociation':
          return this.$filters.getDatasetIcon({ type: item.targetType })
        case 'templateAssociation':
          return this.$filters.getTemplateIcon(item)
        case 'conceptMapAssociation':
          return this.$filters.getConceptMapIcon(item)
        default:
          return 'mdi-checkbox-blank-circle'
      }
    },
    routeObject (item, key) {
      const routeType = (item?.targetId && item?.targetEffectiveDate)
        ? 'idAndVersion'
        : item?.targetId && this.routes[key]?.id
          ? 'id'
          : null
      if (routeType) {
        const params = {}
        this.routes[key][routeType].props.forEach(prop => {
          params[prop.name] = item[prop.prop]
        })
        if (!params.prefix && item.ident) {
          params.prefix = item.ident
        }

        return {
          name: this.routes[key][routeType].name,
          params
        }
      }
      return null
    },
    sortItemByName (a, b) {
      if (this.$filters.getMatchingTranslation({ value: a }) > this.$filters.getMatchingTranslation({ value: b })) {
        return 1
      }
      if (this.$filters.getMatchingTranslation({ value: a }) < this.$filters.getMatchingTranslation({ value: b })) {
        return -1
      }
      return 0
    },
    closeExpansionDialog () {
      this.modals.expansions.expansion = null
      this.modals.expansions.loading = false
    },
    async deleteExpansionSet (item) {
      const agree = await this.$confirm(this.$t('are-you-sure-this-action-cannot-be-undone'))
      if (!agree) return
      const id = item?.targetId
      if (!id) return console.error('no id found for valueset expansion set to delete')

      try {
        this.loadingDeleteExpansionSet = true
        await this.$store.dispatch('terminology/deleteValueSetExpansion', { id })
        if (Array.isArray(this.usageList?.[0]?.valueSetExpansionAssociation)) {
          this.usageList[0].valueSetExpansionAssociation = this.usageList[0].valueSetExpansionAssociation.filter(item => item.targetId !== id)
          if (!this.usageList[0].valueSetExpansionAssociation.length) {
            this.$delete(this.usageList[0], 'valueSetExpansionAssociation')
          }
          if (!Object.keys(this.usageList[0]).length) {
            this.usageList = []
          }
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        this.$oops(errorMessage)
      } finally {
        this.loadingDeleteExpansionSet = false
      }
    },
    async openExpansionDialog (id) {
      if (this.modals.expansions.loading) return

      try {
        this.modals.expansions.loading = true
        const res = await TerminologyService.getValueSetExpansionSet({ id })
        this.modals.expansions.expansion = res.data || null
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        this.$oops(errorMessage)
      } finally {
        this.modals.expansions.loading = false
      }

      await this.$nextTick()

      if (this.$refs.expansionDialog) {
        this.$refs.expansionDialog.openDialog()
      }
    }
  },
  created () {
    if (this.expanded) {
      this.fetchUsageList({ refresh: true })
    }
  }
}
</script>
